import React, { useMemo } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect, useLocation, useHistory } from 'react-router-dom';
import { Header } from './components/Layout/Header';
import { Sidebar } from './components/Layout/Sidebar';
import { StudentPageAds } from './components/ads/StudentPageAds';
import { DynamicComponent } from './components/DynamicComponent';
import { useConfig } from './hooks/useConfig';
import { useNavigationModules } from './hooks/useNavigationModules';
import { useAuth } from './context/AuthContext';
import { registerAllModules } from './registerModules';
import { LoginPage } from './pages/LoginPage';
import { LandingPage } from './pages/LandingPage';
import { CareersPage } from './pages/CareersPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { UserProfilePage } from './pages/UserProfilePage';
import { ForceChangePasswordPage } from './pages/ForceChangePasswordPage';
import { ZohoOAuthCallbackPage } from './pages/ZohoOAuthCallbackPage';
import { GoogleOAuthCallbackPage } from './pages/GoogleOAuthCallbackPage';
import { VerifyDocumentPage } from './pages/VerifyDocumentPage';
import { VerifyPickupPage } from './pages/VerifyPickupPage';
import { AccessDeniedPage } from './pages/AccessDeniedPage';
import { SuperAdminImpersonationHandoff } from './components/auth/SuperAdminImpersonationHandoff';
import { ModuleConfig, User } from './types/config';
import { TenantCurrencyProvider } from './context/TenantCurrencyContext';
import { ReferenceDataProvider } from './context/ReferenceDataContext';
import {
  canUserOpenModule,
  applyBackendModuleNames,
  explainModuleAccess,
} from './utils/moduleAccess';
import { resolveHomeModule } from './config/homeModule';
import { isTenantHost } from './utils/tenantSlug';

registerAllModules();

function AuthenticatedShell({ user }: { user: User }) {
  const { config } = useConfig();
  const { logout, moduleAccessError } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const { modules: navigationModules, loading: navLoading } = useNavigationModules();

  const handleLogout = () => {
    logout();
    history.replace('/login');
  };

  const modulesWithLabels = useMemo(() => {
    return applyBackendModuleNames(navigationModules, user);
  }, [navigationModules, user]);

  // Sidebar order comes from the DB catalog (`tenant_sidebar_modules` sort order).
  const allowedModules = modulesWithLabels.filter((m: ModuleConfig) => canUserOpenModule(m, user));
  const moduleData = useMemo(() => ({ user }), [user]);

  React.useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (process.env.REACT_APP_DEBUG_RBAC !== 'true') return;
    // RBAC diagnostic: compare DB module_access vs what the sidebar shows.
    // eslint-disable-next-line no-console
    console.groupCollapsed(
      `[RBAC] ${user.email} · role=${user.role} · module_access entries=${user.moduleAccess?.length ?? 0}`
    );
    // eslint-disable-next-line no-console
    console.table(
      (user.moduleAccess ?? []).map((m) => ({
        module_code: m.module_code,
        enabled: m.enabled,
        access_level: m.access_level,
      }))
    );
    // eslint-disable-next-line no-console
    console.table(explainModuleAccess(modulesWithLabels, user));
    // eslint-disable-next-line no-console
    console.groupEnd();
  }, [user, modulesWithLabels]);
  const homeModule = useMemo(
    () => resolveHomeModule(allowedModules, user),
    [allowedModules, user]
  );
  const fallbackPath = '/';

  if (navLoading) {
    return (
      <div className="royal-loading min-h-screen">
        <div className="royal-loading-spinner" aria-hidden />
        <p className="font-display text-lg tracking-wide">Loading navigation…</p>
      </div>
    );
  }

  return (
    <TenantCurrencyProvider>
      <ReferenceDataProvider>
        <div className="min-h-screen flex flex-col">
          {config.layout.header.enabled && (
            <Header
              config={config.layout.header}
              user={user}
              onLogout={handleLogout}
            />
          )}

          <div className="flex flex-1 min-h-0">
            {config.layout.sidebar.enabled && (
              <Sidebar
                config={allowedModules}
                sidebarConfig={config.layout.sidebar}
                user={user}
              />
            )}

            <main className="flex flex-1 min-h-0 flex-col overflow-y-auto royal-main">
              <div className="flex-1 min-h-0">
                {moduleAccessError && (
                  <div className="p-4 pb-0">
                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                      {moduleAccessError}
                    </div>
                  </div>
                )}
                <Switch>
                  <Route path="/verify/pickup/:pin" exact render={() => <VerifyPickupPage />} />
                  <Route path="/verify/:code" exact render={() => <VerifyDocumentPage />} />
                  <Route path="/profile" exact render={() => <UserProfilePage user={user} />} />
                  {allowedModules.length === 0 ? (
                    <Route
                      render={() => (
                        <div className="p-6">
                          <div className="bg-white border border-amber-200 rounded-lg p-5 text-sm text-amber-800">
                            {moduleAccessError
                              ? 'Modules could not be loaded because access verification failed.'
                              : 'No modules are available for this account. Ask an administrator to enable screen access.'}
                          </div>
                        </div>
                      )}
                    />
                  ) : (
                    // Must be an array, not a fragment: react-router v5 Switch only
                    // route-matches its direct children, and a fragment child renders
                    // every route inside it at once (duplicated dashboard).
                    [
                      <Redirect
                        key="__teachers-to-staff"
                        from="/teachers"
                        to="/staff?tab=teachers"
                      />,
                      <Route
                        key="__home"
                        path="/"
                        exact
                        render={() => {
                          if (!homeModule) return null;
                          // Redirect to the module's real path so the URL always
                          // reflects the page being shown (and the sidebar highlights it).
                          if (homeModule.path && homeModule.path !== '/') {
                            return <Redirect to={homeModule.path} />;
                          }
                          return <DynamicComponent moduleConfig={homeModule} data={moduleData} />;
                        }}
                      />,
                      ...allowedModules.map((module: ModuleConfig) => (
                        <Route
                          key={module.id}
                          path={module.path}
                          exact={module.id !== 'staff'}
                          render={() => (
                            <DynamicComponent moduleConfig={module} data={moduleData} />
                          )}
                        />
                      )),
                      // Known screens the user cannot open → stay logged in with a clear deny.
                      ...modulesWithLabels
                        .filter((module) => !canUserOpenModule(module, user))
                        .map((module: ModuleConfig) => (
                          <Route
                            key={`denied-${module.id}`}
                            path={module.path}
                            exact={module.id !== 'staff'}
                            render={() => (
                              <AccessDeniedPage
                                path={module.path}
                                homePath={homeModule?.path || fallbackPath}
                              />
                            )}
                          />
                        )),
                      <Route
                        key="__fallback"
                        render={() =>
                          location.pathname === fallbackPath ? null : (
                            <AccessDeniedPage
                              path={location.pathname}
                              homePath={homeModule?.path || fallbackPath}
                            />
                          )
                        }
                      />,
                    ]
                  )}
                </Switch>
              </div>
              <StudentPageAds user={user} />
            </main>
          </div>
        </div>
      </ReferenceDataProvider>
    </TenantCurrencyProvider>
  );
}

function AppRoutes() {
  const { loading: configLoading } = useConfig();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const location = useLocation();

  if (configLoading || authLoading) {
    return (
      <div className="royal-loading">
        <div className="royal-loading-spinner" aria-hidden />
        <p className="font-display text-lg tracking-wide">Loading your portal…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    const onTenantHost = isTenantHost();
    return (
      <>
        <SuperAdminImpersonationHandoff />
        <Switch>
          <Route path="/verify/pickup/:pin" exact render={() => <VerifyPickupPage />} />
          <Route path="/verify/:code" exact render={() => <VerifyDocumentPage />} />
          <Route path="/login" exact render={() => <LoginPage />} />
          <Route path="/careers" exact render={() => <CareersPage />} />
          <Route path="/forgot-password" exact render={() => <ForgotPasswordPage />} />
          <Route path="/reset-password" exact render={() => <ResetPasswordPage />} />
          <Route path="/login/zoho/callback" exact render={() => <ZohoOAuthCallbackPage />} />
          <Route path="/login/google/callback" exact render={() => <GoogleOAuthCallbackPage />} />
          {!onTenantHost && <Route path="/" exact render={() => <LandingPage />} />}
          <Redirect
            to={{
              pathname: onTenantHost ? '/login' : '/',
              state: { from: location },
            }}
          />
        </Switch>
      </>
    );
  }

  if (!user) {
    return <Redirect to="/login" />;
  }

  if (user.mustChangePassword) {
    return (
      <Switch>
        <Route path="/change-password" exact render={() => <ForceChangePasswordPage />} />
        <Redirect to="/change-password" />
      </Switch>
    );
  }

  return (
    <Switch>
      <Route path="/verify/pickup/:pin" exact render={() => <VerifyPickupPage />} />
      <Route path="/verify/:code" exact render={() => <VerifyDocumentPage />} />
      <Route path="/careers" exact render={() => <CareersPage />} />
      <Route path="/login" exact render={() => <Redirect to="/" />} />
      <Route path="/forgot-password" exact render={() => <Redirect to="/" />} />
      <Route path="/reset-password" exact render={() => <Redirect to="/" />} />
      <Route path="/change-password" exact render={() => <Redirect to="/" />} />
      <Route path="/login/zoho/callback" exact render={() => <Redirect to="/" />} />
      <Route path="/login/google/callback" exact render={() => <Redirect to="/" />} />
      <Route render={() => <AuthenticatedShell user={user} />} />
    </Switch>
  );
}

function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;
